import { useState, type FormEvent, type ReactNode } from 'react'
import { ApiError } from '../api/client'

const PIN_RE = /^\d{6}$/

/**
 * PIN de 6 dígitos + confirmación. Lo comparten `/set-pin` (link del mail) y
 * el paso opcional "Creá tu PIN" de `PlayBridge`: el formato y los mensajes de
 * error son los mismos, cambia sólo a qué endpoint se manda.
 *
 * `onSubmitPin` hace la llamada; si tira, el form traduce el error. Devolver
 * `false` desde `onError` deja que el caller lo maneje (ej. token vencido).
 */
export function PinForm({
  submitLabel,
  onSubmitPin,
  onError,
  secondary,
}: {
  submitLabel: string
  onSubmitPin: (pin: string) => Promise<void>
  onError?: (err: unknown) => boolean
  secondary?: ReactNode
}) {
  const [pin, setPin] = useState('')
  const [confirmPin, setConfirmPin] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const pinValid = PIN_RE.test(pin)
  const pinsMatch = pin === confirmPin
  const canSubmit = pinValid && pinsMatch && !busy

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!canSubmit) return
    setBusy(true)
    setError(null)
    try {
      await onSubmitPin(pin)
    } catch (err) {
      if (onError && !onError(err)) return
      setError(pinErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={onSubmit} className="mt-md flex flex-col gap-sm">
      {error && <div className="banner-err">{error}</div>}

      <label className="flex flex-col gap-xs text-sm">
        PIN (6 dígitos)
        <input
          type="text"
          inputMode="numeric"
          pattern="\d{6}"
          maxLength={6}
          autoComplete="off"
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="••••••"
          className="input tracking-[0.5em]"
          autoFocus
        />
      </label>

      <label className="flex flex-col gap-xs text-sm">
        Confirmar PIN
        <input
          type="text"
          inputMode="numeric"
          pattern="\d{6}"
          maxLength={6}
          autoComplete="off"
          value={confirmPin}
          onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="••••••"
          className="input tracking-[0.5em]"
        />
      </label>

      {confirmPin.length === 6 && !pinsMatch && (
        <span className="text-xs text-danger">Los PIN no coinciden.</span>
      )}
      {pin.length > 0 && pin.length < 6 && (
        <span className="text-xs text-text-muted">Faltan {6 - pin.length} dígitos.</span>
      )}

      <button type="submit" disabled={!canSubmit} className="btn-primary mt-xs">
        {busy ? 'Guardando…' : submitLabel}
      </button>
      {secondary}
    </form>
  )
}

export function pinErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 422) return 'El PIN tiene que ser exactamente 6 dígitos numéricos.'
    if (err.message === 'pin_taken') return 'Ese PIN ya lo usa otro jugador de tu club. Elegí otro.'
    if (err.message === 'pin_exists') return 'Ya tenés un PIN. Para cambiarlo pedí el link por mail.'
    return err.message
  }
  return 'Error de red.'
}
